import { FC, useMemo, useState } from 'react';
import {
  faArrowDownLong,
  faArrowUpLong,
  faBed,
  faCheck,
  faChevronDown,
  faChevronUp,
  faStar,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import TripBuilderClientHotelDetails2, {
  formatFreeCancellationLabel,
  getTripBuilderHotelImageUrls,
} from './TripBuilderClientHotelDetails2';
import { dateFormat, i18nTB } from 'utils/shared/utils';
import { getTripPreviewHotelByCardKey, TripPreviewHotelCard } from 'utils/shared/tripResultBuilderPreview';
import { getBlobUrl, resolveGalleryImageUrl } from 'utils/shared/tripBuilderImages';
import { ProposalData } from 'ts/shared/interfaces/ProposalData';
import { Carousel } from 'react-responsive-carousel';
import { imgBg } from 'constants/generic';

const formatShortDate = (value: string, locale: string) =>
  value ? dateFormat(value, 'dd.MM.yy', locale) : '-';

const parsePriceNumber = (label?: string | null) => {
  if (!label) {
    return null;
  }
  const match = label.replace(/,/g, '').match(/[\d.]+/);
  return match ? Number(match[0]) : null;
};

const getPriceCurrency = (label: string) => {
  const currencyMatch = label.match(/[^\d.,\s]+/g);
  return currencyMatch?.[currencyMatch.length - 1] || '₪';
};

const formatPriceWithCurrency = (amountLabel: string, currency: string) =>
  `${amountLabel} ${currency}`;

const formatTotalBadgePrice = (label: string) => {
  const amountLabel = label.replace(/[^\d.,]/g, '').replace(/^[.,]+|[.,]+$/g, '');
  return amountLabel ? formatPriceWithCurrency(amountLabel, getPriceCurrency(label)) : label;
};

const formatPerNightPrice = (totalLabel: string | null, nights: number | null | undefined) => {
  if (!totalLabel || !nights || nights <= 0) {
    return null;
  }
  const amount = parsePriceNumber(totalLabel);
  if (amount == null) {
    return null;
  }
  return formatPriceWithCurrency(
    Math.round(amount / nights).toLocaleString('en-US'),
    getPriceCurrency(totalLabel),
  );
};

type TripBuilderClientHotelCard2Props = {
  card: TripPreviewHotelCard;
  checkIn: string;
  checkOut: string;
  hidePrices?: boolean;
  selectedRoomIndex: number;
  productNote?: string;
  externalParam_1?: string;
  detailsMode?: boolean;
  onOpenDetails?: () => void;
  onCloseDetails?: () => void;
  onSelect: () => void;
  onSelectRoom: (roomIndex: number) => void;
};

const TripBuilderClientHotelCard2: FC<TripBuilderClientHotelCard2Props> = ({
  card,
  checkIn,
  checkOut,
  hidePrices,
  selectedRoomIndex,
  productNote,
  externalParam_1,
  detailsMode,
  onOpenDetails,
  onCloseDetails,
  onSelect,
  onSelectRoom,
}) => {
  const { t, i18n } = useTranslation(undefined, { i18n: i18nTB });
  const blobUrl = getBlobUrl();
  const hotelImageSrc = card.logoCode
    ? resolveGalleryImageUrl(blobUrl, card.logoCode)
    : imgBg;
  const selectedRoom = (card.rooms || [])[selectedRoomIndex];
  const displayPrice =
    selectedRoom?.priceLabel ?? (card.priceLabel ? card.priceLabel.replace(/^From\s+/i, '') : null);
  const perNightPrice = formatPerNightPrice(displayPrice, card.nights);
  const rating = card.tripAdvisorHotel?.rating ?? card.star;
  const imageUrls = getTripBuilderHotelImageUrls(card, i18n.language);
  const [rejectedImageUrls, setRejectedImageUrls] = useState<Set<string>>(() => new Set());
  const galleryImageUrls = useMemo(() => {
    const urls = (imageUrls.length ? imageUrls : [hotelImageSrc]).filter(
      (url) => !rejectedImageUrls.has(url),
    );
    return urls.length ? urls : [imgBg];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelImageSrc, imageUrls, imgBg, rejectedImageUrls]);
  const gallerySlides = useMemo(() => {
    const slides: string[][] = [];

    for (let index = 0; index < galleryImageUrls.length; index += 2) {
      slides.push(galleryImageUrls.slice(index, index + 2));
    }

    return slides;
  }, [galleryImageUrls]);

  const hasDetails =
    !!productNote ||
    !!imageUrls.length ||
    !!(card.rooms || []).some((room) => room.roomLabel || room.boardLabel || room.cxlLabel);
  const roomCxl =
    selectedRoom?.cxlLabel && selectedRoom.cxlType
      ? { cxlLabel: selectedRoom.cxlLabel, cxlType: selectedRoom.cxlType }
      : (card.rooms || []).find((room) => room.cxlLabel && room.cxlType);

  const detailsContent = (
    <TripBuilderClientHotelDetails2
      card={card}
      selectedRoomIndex={selectedRoomIndex}
      productNote={productNote}
      onSelectRoom={onSelectRoom}
      onClose={onCloseDetails}
    />
  );

  const cxlRow = !!roomCxl?.cxlLabel && (
    <div
      className={
        'tb2-hotel-card-stay-row tb2-hotel-card-stay-row-cxl' +
        (roomCxl.cxlType === 'free'
          ? ' tb2-hotel-card-stay-row-cxl-free'
          : ' tb2-hotel-card-stay-row-cxl-full')
      }
    >
      <span
        className={
          'tb2-hotel-card-cxl-icon' +
          (roomCxl.cxlType === 'full' ? ' tb2-hotel-card-cxl-icon-full' : '')
        }
        aria-hidden="true"
      >
        <FontAwesomeIcon icon={roomCxl.cxlType === 'free' ? faCheck : faXmark} />
      </span>
      <span
        className={
          'tb2-hotel-card-stay-text tb2-hotel-card-stay-text-cxl' +
          (roomCxl.cxlType === 'free'
            ? ' tb2-hotel-card-stay-text-cxl-free'
            : ' tb2-hotel-card-stay-text-cxl-full')
        }
      >
        {roomCxl.cxlType === 'free'
          ? formatFreeCancellationLabel(roomCxl.cxlLabel, i18n.language, t)
          : t('trip_builder.full_cancellation_fee')}
      </span>
    </div>
  );

  const pricesBlock = !hidePrices && !!displayPrice && (
    <div className="tb2-hotel-card-prices">
      {!!perNightPrice && (
        <span className="tb2-hotel-card-price tb2-hotel-card-price-night">
          {t('trip_builder.per_night', { price: perNightPrice })}
        </span>
      )}
      <span className="tb2-hotel-card-price tb2-hotel-card-price-total">
        {t('trip_builder.total_price', { price: formatTotalBadgePrice(displayPrice) })}
      </span>
    </div>
  );

  const selectButton = (
    <button
      type="button"
      className={'tb2-hotel-card-select' + (card.isActive ? ' tb2-hotel-card-select-active' : '')}
      onClick={onSelect}
    >
      {card.isActive ? t('selected') : t('trip_builder.select_hotel')}
    </button>
  );

  return (
    <article
      className={
        'tb2-hotel-card' +
        (card.isActive ? ' tb2-hotel-card-active' : '') +
        (detailsMode ? ' tb2-hotel-card-details-mode' : '')
      }
    >
      {!detailsMode && (
        <div className="tb2-hotel-card-label">{t('trip_builder.hotel_for_selection')}</div>
      )}

      <div className="tb2-hotel-card-media">
        {detailsMode ? (
          <div className="tb2-hotel-card-media-carousel" style={{ direction: 'ltr' }}>
            <Carousel showIndicators={false} showThumbs={false} showStatus={false}>
              {gallerySlides.map((slideUrls, slideIndex) => (
                <div
                  className={
                    'tb2-hotel-card-media-slide' +
                    (slideUrls.length === 1 ? ' tb2-hotel-card-media-slide-single' : '')
                  }
                  key={slideIndex}
                >
                  {slideUrls.map((url, index) => (
                    <img
                      className="tb2-hotel-card-img"
                      src={url}
                      alt=""
                      key={`${url}-${index}`}
                      onLoad={(event) => {
                        const img = event.currentTarget;
                        if (
                          url !== imgBg &&
                          (img.naturalWidth < 100 || img.naturalHeight < 100)
                        ) {
                          setRejectedImageUrls((prev) => {
                            if (prev.has(url)) {
                              return prev;
                            }
                            const source = imageUrls.length ? imageUrls : [hotelImageSrc];
                            const remainingAfter = source.filter(
                              (item) => item !== url && !prev.has(item),
                            ).length;
                            if (remainingAfter < 2) {
                              return prev;
                            }
                            const next = new Set(prev);
                            next.add(url);
                            return next;
                          });
                        }
                        img.style.display = 'block';
                      }}
                      onError={(event) => {
                        event.currentTarget.src = imgBg;
                      }}
                    />
                  ))}
                </div>
              ))}
            </Carousel>
          </div>
        ) : (
          <img
            className="tb2-hotel-card-img"
            src={hotelImageSrc}
            alt=""
            onError={(event) => {
              event.currentTarget.src = imgBg;
            }}
          />
        )}
        <div className="tb2-hotel-card-media-overlay">
          <div className="tb2-hotel-card-media-bottom">
            {rating != null && (
              <span className="tb2-hotel-card-rating">
                <FontAwesomeIcon icon={faStar} />
                {rating}
              </span>
            )}
            <span className="tb2-hotel-card-name" dir="ltr">
              {card.name}
            </span>
          </div>
        </div>
      </div>

      <div className="tb2-hotel-card-divider" aria-hidden="true" />

      {hasDetails && !detailsMode && (
        <div className="tb2-hotel-card-details">
          <button type="button" className="tb2-hotel-toggle" onClick={onOpenDetails}>
            <span className="tb2-hotel-toggle-label">
              {t('trip_builder.hotel_details_link')}
              <FontAwesomeIcon icon={faChevronDown} aria-hidden="true" />
            </span>
            <span className="tb2-hotel-toggle-line" aria-hidden="true" />
          </button>
        </div>
      )}

      {hasDetails && detailsMode && (
        <div className="tb2-hotel-card-details tb2-hotel-card-details-expanded">
          <div className="tb2-hotel-card-details-panel">{detailsContent}</div>
        </div>
      )}

      {!detailsMode && (
        <>
          <div className="tb2-hotel-card-stay">
            <div className="tb2-hotel-card-stay-row">
              <FontAwesomeIcon
                icon={faArrowDownLong}
                className="tb2-hotel-card-stay-icon tb2-hotel-card-stay-icon-checkin"
                aria-hidden="true"
              />
              <span className="tb2-hotel-card-stay-text">
                {t('trip_builder.check_in')}: {formatShortDate(checkIn, i18n.language)}
              </span>
            </div>
            <div className="tb2-hotel-card-stay-row">
              <FontAwesomeIcon
                icon={faArrowUpLong}
                className="tb2-hotel-card-stay-icon tb2-hotel-card-stay-icon-checkout"
                aria-hidden="true"
              />
              <span className="tb2-hotel-card-stay-text">
                {t('trip_builder.check_out')}: {formatShortDate(checkOut, i18n.language)}
              </span>
            </div>
            {!!card.nights && (
              <div className="tb2-hotel-card-stay-row">
                <FontAwesomeIcon icon={faBed} className="tb2-hotel-card-stay-icon" aria-hidden="true" />
                <span className="tb2-hotel-card-stay-text">
                  {t('trip_builder.total_nights', {
                    count: card.nights,
                    nightsLabel: card.nights === 1 ? t('night') : t('nights'),
                  })}
                </span>
              </div>
            )}
            {cxlRow}
          </div>

          {pricesBlock}
          {selectButton}
        </>
      )}

      {detailsMode && (
        <>
          <div className="tb2-hotel-card-meta">
            {cxlRow}
            {pricesBlock}
          </div>

          <div className="tb2-hotel-card-footer">
            {selectButton}
            <div className="tb2-hotel-card-footer-date">
              <FontAwesomeIcon
                icon={faArrowDownLong}
                className="tb2-hotel-card-stay-icon tb2-hotel-card-stay-icon-checkin"
                aria-hidden="true"
              />
              <span>
                {t('trip_builder.check_in')}: {formatShortDate(checkIn, i18n.language)}
              </span>
            </div>
            <div className="tb2-hotel-card-footer-date">
              <FontAwesomeIcon
                icon={faArrowUpLong}
                className="tb2-hotel-card-stay-icon tb2-hotel-card-stay-icon-checkout"
                aria-hidden="true"
              />
              <span>
                {t('trip_builder.check_out')}: {formatShortDate(checkOut, i18n.language)}
              </span>
            </div>
            {!!card.nights && (
              <div className="tb2-hotel-card-footer-nights">
                <FontAwesomeIcon icon={faBed} className="tb2-hotel-card-stay-icon" aria-hidden="true" />
                <span>
                  {t('trip_builder.total_nights', {
                    count: card.nights,
                    nightsLabel: card.nights === 1 ? t('night') : t('nights'),
                  })}
                </span>
              </div>
            )}
          </div>
        </>
      )}
    </article>
  );
};

export type TripBuilderClientDestinationHotels2Props = {
  city: string;
  checkIn: string;
  checkOut: string;
  hotels: TripPreviewHotelCard[];
  slotImageUrl?: string;
  hidePrices?: boolean;
  selectedRoomByHotel: Record<string, number>;
  proposal: ProposalData;
  externalParam_1?: string;
  onSelectHotel: (hotelKey: string) => void;
  onSelectRoom: (hotelKey: string, roomIndex: number) => void;
};

const TripBuilderClientDestinationHotels2: FC<TripBuilderClientDestinationHotels2Props> = ({
  city,
  hotels,
  slotImageUrl,
  checkIn,
  checkOut,
  hidePrices,
  selectedRoomByHotel,
  proposal,
  externalParam_1,
  onSelectHotel,
  onSelectRoom,
}) => {
  const { t, i18n } = useTranslation(undefined, { i18n: i18nTB });
  const blobUrl = getBlobUrl();
  const [expanded, setExpanded] = useState(true);
  const [detailsHotelKey, setDetailsHotelKey] = useState<string | null>(null);
  const isRtl = i18n.language === 'he';
  const bannerUrl = useMemo(
    () => (slotImageUrl ? resolveGalleryImageUrl(blobUrl, slotImageUrl) : ''),
    [blobUrl, slotImageUrl],
  );

  if (!hotels.length) {
    return null;
  }

  return (
    <section className="tb2-destination" dir={isRtl ? 'rtl' : 'ltr'}>
      {!!bannerUrl && (
        <div className="tb2-destination-banner">
          <img className="tb2-destination-banner-img" src={bannerUrl} alt="" />
        </div>
      )}

      <div className="tb2-destination-head">
        <div className="tb2-destination-city">{city}</div>
        {!detailsHotelKey && (
          <button
            type="button"
            className="tb2-destination-toggle"
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded ? t('trip_builder.close_hotels') : t('trip_builder.open_hotels')}
            <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
          </button>
        )}
      </div>

      {expanded && (
        <div className="tb2-destination-body">
          <div
            className={
              'tb2-destination-cards' +
              (hotels.length === 1 && !detailsHotelKey ? ' tb2-destination-cards-single' : '') +
              (detailsHotelKey ? ' tb2-destination-cards-details' : '')
            }
          >
            {hotels.map((card) => (
              <TripBuilderClientHotelCard2
                key={card.key}
                card={card}
                checkIn={checkIn}
                checkOut={checkOut}
                hidePrices={hidePrices}
                selectedRoomIndex={selectedRoomByHotel[card.key] ?? 0}
                productNote={(() => {
                  const note = getTripPreviewHotelByCardKey(proposal, card.key)?.displayNote?.name?.trim();
                  return note || undefined;
                })()}
                externalParam_1={externalParam_1}
                detailsMode={detailsHotelKey === card.key}
                onOpenDetails={() => setDetailsHotelKey(card.key)}
                onCloseDetails={() => setDetailsHotelKey(null)}
                onSelect={() => onSelectHotel(card.key)}
                onSelectRoom={(roomIndex) => onSelectRoom(card.key, roomIndex)}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default TripBuilderClientDestinationHotels2;
